import React, { useState, useEffect } from "react";
import { TextField, Button, MenuItem, Paper } from "@mui/material";
import { useSnackbar } from "notistack";
import driverApi from "../../apis/driverApi";
import styles from "./DriverProfile.module.scss";

const POPULAR_BANKS = [
  "Vietcombank (VCB)",
  "Techcombank (TCB)",
  "MB Bank (MB)",
  "VietinBank (CTG)",
  "BIDV",
  "ACB",
  "VPBank",
  "TPBank",
  "Sacombank",
  "HDBank",
  "VIB",
  "MSB",
  "SeABank",
  "Agribank",
  "Khác",
];

const DriverProfile = () => {
  const { enqueueSnackbar } = useSnackbar();
  const driverId = localStorage.getItem("driverId") || "";
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    bankName: "",
    bankAccount: "",
    accountHolder: "",
  });

  const [loading, setLoading] = useState(false);

  const [customBank, setCustomBank] = useState("");

  const getPartnerId = (): string => {
    return process.env.REACT_APP_ENV === "production" ? "01G486CQAV7VW2FK3DHZV1T2FG" : "01G4866BBJGYR1JH8E6RPYEW2C";
  };

  useEffect(() => {
    // If name is not yet in state, fetch driver list to resolve exact name created by admin
    driverApi.listDrivers(getPartnerId()).then((res) => {
      if (res.data?.code === 0 && res.data?.data) {
        const found = res.data.data.find((d: any) => d.id === driverId);
        if (found) {
          localStorage.setItem("driverName", found.name);
          localStorage.setItem("driverPhone", found.phone);
          setFormData((prev) => ({
            ...prev,
            name: found.name,
            phone: found.phone,
          }));
        }
      }
    }).catch(() => {});

    // Load local cache first for instant feedback
    const savedBank = localStorage.getItem(`driver_bank_${driverId}`);
    if (savedBank) {
      try {
        const parsed = JSON.parse(savedBank);
        if (parsed.bankName && !POPULAR_BANKS.includes(parsed.bankName)) {
          setFormData((prev) => ({ ...prev, ...parsed, bankName: "Khác" }));
          setCustomBank(parsed.bankName);
        } else {
          setFormData((prev) => ({ ...prev, ...parsed }));
        }
      } catch (e) {}
    }

    // Fetch live data from backend
    if (driverId) {
      driverApi.getDriver(driverId).then((res) => {
        if (res.data?.data) {
          const d = res.data.data;
          const isOther = d.bankName && !POPULAR_BANKS.includes(d.bankName);
          setFormData((prev) => ({
            ...prev,
            name: d.name || prev.name,
            phone: d.phone || prev.phone,
            bankName: isOther ? "Khác" : d.bankName || prev.bankName,
            bankAccount: d.bankAccount || prev.bankAccount,
            accountHolder: d.accountHolder || prev.accountHolder,
          }));
          if (isOther) {
            setCustomBank(d.bankName);
          }
        }
      }).catch(() => {});
    }
  }, [driverId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveBankName = formData.bankName === "Khác" ? customBank.trim() : formData.bankName;

    if (!effectiveBankName || !formData.bankAccount) {
      enqueueSnackbar("Vui lòng điền thông tin tên ngân hàng và số tài khoản", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      // Save locally to synchronize across admin & driver views
      const bankInfo = {
        driverId,
        bankName: effectiveBankName,
        bankAccount: formData.bankAccount,
        accountHolder: formData.accountHolder || formData.name,
      };
      localStorage.setItem(`driver_bank_${driverId}`, JSON.stringify(bankInfo));

      // Synchronize with backend API
      try {
        await driverApi.updateDriver({
          id: driverId,
          bankName: effectiveBankName,
          bankAccount: formData.bankAccount,
          accountHolder: formData.accountHolder || formData.name,
        });
      } catch (beErr) {
        console.warn("Backend update API pending, synchronized via local store:", beErr);
      }

      enqueueSnackbar("Cập nhật thông tin ngân hàng thành công! Bên Admin đã có thể xem được.", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Lỗi khi cập nhật thông tin", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <Paper className={styles.profileCard} elevation={2}>
        <div className={styles.header}>
          <h2>Thông tin Cộng tác viên & Ngân hàng</h2>
          <p>Cập nhật số tài khoản để nhận tiền công vận chuyển tự động từ ReShare.</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.sectionTitle}>Thông tin cá nhân</div>
          <div className={styles.row}>
            <TextField
              label="Họ và tên"
              value={formData.name || localStorage.getItem("driverName") || "Cộng tác viên"}
              disabled
              fullWidth
            />
            <TextField
              label="Số điện thoại tài khoản"
              value={formData.phone || localStorage.getItem("driverPhone") || "0123456789"}
              disabled
              fullWidth
            />
          </div>

          <div className={styles.sectionTitle} style={{ marginTop: 24 }}>
            Thông tin tài khoản ngân hàng nhận tiền
          </div>
          <div className={styles.row}>
            <TextField
              select
              label="Chọn ngân hàng"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              fullWidth
              required
            >
              {POPULAR_BANKS.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Số tài khoản ngân hàng"
              placeholder="VD: 01234567899"
              value={formData.bankAccount}
              onChange={(e) => setFormData({ ...formData, bankAccount: e.target.value })}
              fullWidth
              required
            />
          </div>

          {formData.bankName === "Khác" && (
            <div className={styles.row}>
              <TextField
                label="Nhập tên ngân hàng của bạn"
                placeholder="VD: Ngân hàng Quốc tế VIB, PVcomBank, KienlongBank,..."
                value={customBank}
                onChange={(e) => setCustomBank(e.target.value)}
                fullWidth
                required
              />
            </div>
          )}

          <div className={styles.row}>
            <TextField
              label="Tên chủ tài khoản (In hoa không dấu)"
              placeholder="VD: NGUYEN VAN A"
              value={formData.accountHolder}
              onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value.toUpperCase() })}
              fullWidth
            />
          </div>

          <div className={styles.note}>
            💡 <b>Lưu ý:</b> Vui lòng kiểm tra kỹ số tài khoản ngân hàng và tên ngân hàng để đảm bảo công ty đối soát và chuyển tiền thanh toán chính xác.
          </div>

          <div className={styles.actions}>
            <Button type="submit" variant="contained" color="success" size="large" disabled={loading}>
              {loading ? "Đang lưu..." : "Lưu thông tin ngân hàng"}
            </Button>
          </div>
        </form>
      </Paper>
    </div>
  );
};

export default DriverProfile;
